import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// -----------------------------------------------------------------------------
// Phase 22 — Sesión en cookie httpOnly (mayo 2026)
// -----------------------------------------------------------------------------
// El JWT ya NO se guarda en localStorage: el backend lo emite como cookie
// `cjo_session` httpOnly cuando se hace POST /api/auth/login. El navegador
// la envía automáticamente en las peticiones same-origin gracias a
// `withCredentials: true`.
//
// Beneficios:
//   - Inaccesible desde JS → mitigación de exfiltración por XSS.
//   - El backend mantiene compatibilidad con Authorization header para
//     herramientas server-to-server (curl, requests, testing automatizado).
//
// Decisiones (ver plan.md Phase 22):
//   - C1: SameSite=Lax sin token CSRF explícito (uso interno clínico).
//   - R1: Token único largo (7 días), sin refresh silencioso.
//
// Sólo persiste en localStorage el OBJETO USUARIO para que el AppShell se
// pueda rehidratar instantáneamente (la cookie sigue siendo la fuente de
// verdad: `/api/auth/me` valida en cada navegación inicial).
// -----------------------------------------------------------------------------

export const api = axios.create({
  baseURL: BACKEND_URL,
  timeout: 120000,        // 2 minutos global
  withCredentials: true,  // CRÍTICO: para que el browser envíe la cookie cjo_session
});

const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';

function warnStorage(action, err) {
  if (isDev && typeof console !== 'undefined' && console.warn) {
    console.warn(`[cjo:storage] ${action} failed (probably privacy mode):`, err?.message || err);
  }
}

// One-time migration: si quedó un JWT viejo en localStorage de versiones
// pre-Phase 22, lo borramos al cargar el módulo. La sesión efectiva está
// ahora en la cookie httpOnly.
try {
  if (typeof localStorage !== 'undefined' && localStorage.getItem('cjo_token')) {
    localStorage.removeItem('cjo_token');
  }
} catch (e) {
  warnStorage('remove legacy cjo_token', e);
}

// El usuario sí lo persistimos para evitar parpadeos al recargar:
// la cookie httpOnly es la fuente de verdad; este objeto es solo caché de UI.
export function getStoredUser() {
  try { const u = localStorage.getItem('cjo_user'); return u ? JSON.parse(u) : null; } catch (e) { warnStorage('getItem cjo_user', e); return null; }
}

export function setStoredUser(user) {
  try {
    if (user) localStorage.setItem('cjo_user', JSON.stringify(user));
    else localStorage.removeItem('cjo_user');
  } catch (e) { warnStorage('setStoredUser', e); }
}

/**
 * Limpia todo rastro de sesión del lado cliente (cache de usuario).
 * El logout real se hace en el backend con `POST /api/auth/logout`,
 * que invalida la cookie httpOnly. Esta función solo borra el caché local.
 */
export function clearClientAuthCache() {
  setStoredUser(null);
}

// Interceptor global de 401: si la cookie ha caducado limpiamos el caché y
// llevamos al login. Hay tres casos en los que NO se debe redirigir:
//
//  - `/api/auth/me`: es la sonda de sesión. Un 401 ahí es su respuesta normal
//    ("no hay sesión"), no una sesión caducada, y AuthProvider ya lo trata.
//    Redirigir aquí rompía cualquier página pública, porque el provider
//    envuelve TODA la app y lanza esa llamada al montar. Es lo que hacía que
//    el paciente que abría el enlace de confirmación de WhatsApp acabara en
//    /login — perdiendo además el `?t=<token>` y sin forma de volver.
//    Las rutas privadas siguen protegidas: RequireAuth redirige por su cuenta.
//  - los endpoints `/api/public/...`, que por definición se sirven sin sesión.
//  - el propio login.
//
// La lista de rutas queda como segunda red, para no dejar al usuario atrapado
// en un bucle si algún otro endpoint devolviera 401 dentro de una página
// pública. Si añades una ruta pública en App.js, añádela también aquí.
// Faltaban /salud y /mis-pasos (revisión de enlaces 2026-09-07): un 401 fuera
// de /api/public dentro de esas páginas mandaba al paciente a /login.
const PUBLIC_ROUTES = ['/login', '/firma-lopd', '/firma-consent', '/firma-consent-link', '/confirmar-cita', '/salud', '/mis-pasos'];

/** Rutas del PACIENTE (sin sesión nunca): ahí no tiene sentido pedir
 *  /api/auth/me al cargar; era un 401 de ruido en cada página pública
 *  (verificación 2026-09-07). /login sí lo pide, para redirigir si ya hay sesión. */
export function esRutaDePaciente(path = window.location.pathname) {
  return PUBLIC_ROUTES.filter((p) => p !== '/login').some((p) => path === p || path.startsWith(p + '/'));
}

const SIN_REDIRECCION = ['/api/auth/login', '/api/auth/me', '/api/public/'];

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';
    if (status === 401 && !SIN_REDIRECCION.some((u) => url.includes(u))) {
      clearClientAuthCache();
      if (typeof window !== 'undefined') {
        const path = window.location.pathname || '/';
        const isPublic = PUBLIC_ROUTES.some(p => path === p || path.startsWith(p + '/'));
        if (!isPublic) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// ---------------------------------------------------------------------------
// Compatibilidad con código heredado
// ---------------------------------------------------------------------------
// (2026-08-27) Los shims deprecated setAuthToken/getStoredToken de Phase 22
// se retiraron: cero imports en toda la app — la transición terminó.

// ---------------------------------------------------------------------------
// Mensaje de error legible a partir de una respuesta fallida
// ---------------------------------------------------------------------------
/**
 * Texto para enseñar al paciente cuando una petición falla (revisión de
 * enlaces 2026-09-07). Hasta ahora las páginas públicas hacían
 * `toast.error(e.response.data.detail)` o `setError(detail)`: con un 422 de
 * FastAPI `detail` es una LISTA de objetos de Pydantic y React revienta al
 * pintarla (página en blanco). Aquí siempre sale un string:
 *   - lista de Pydantic → «campo: mensaje; campo: mensaje»
 *   - string → tal cual
 *   - 413 sin cuerpo JSON (lo corta el proxy) → aviso de tamaño
 *   - nada / red caída → el texto por defecto
 */
export function mensajeError(e, porDefecto = 'No se pudo enviar. Inténtelo de nuevo') {
  const detail = e?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail.trim();
  if (Array.isArray(detail) && detail.length) {
    const partes = detail.map((it) => {
      if (typeof it === 'string') return it;
      const msg = it?.msg || it?.message || '';
      if (!msg) return '';
      const loc = Array.isArray(it?.loc) ? it.loc.filter((s) => s !== 'body' && s !== 'query' && s !== 'path') : [];
      return loc.length ? `${loc.join('.')}: ${msg}` : msg;
    }).filter(Boolean);
    if (partes.length) return partes.join('; ');
  }
  if (detail && typeof detail === 'object' && typeof detail.msg === 'string') return detail.msg;
  if (e?.response?.status === 413) return 'El archivo es demasiado grande. Pruebe con una foto más pequeña.';
  return porDefecto;
}

// ---------------------------------------------------------------------------
// Helpers de formato (sin cambios; quedaban aquí desde antes de Phase 22)
// ---------------------------------------------------------------------------
export function buildAssetUrl(fileId) {
  if (!fileId) return null;
  return `${BACKEND_URL}/api/settings/file/${fileId}`;
}

export function formatEUR(value) {
  const n = Number(value || 0);
  return n.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
}

export function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso.slice(0, 10);
    return d.toLocaleDateString('es-ES');
  } catch { return iso; }
}

export function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-ES');
  } catch { return iso; }
}

export function fullName(p) {
  if (!p) return '';
  return [p.name, p.surname1, p.surname2].filter(Boolean).join(' ').trim();
}
